/**
 * 分辨率展示 / 匹配
 * 后台 value 可能是档位名，也可能是像素宽高，任意档位都走同一套规则：
 * - 480x768 / 864x480 → 480P
 * - 720x1280 / 1280x720 → 720P
 * - 1080x1920 / 1920x1080 → 1080P
 * - 2048x1152 / 2K → 2K
 */

export const isPixelWxHResolution = (value: string): boolean =>
  /^\d+\s*[xX×]\s*\d+$/.test(value.trim())

/** 解析短边像素：720P / 720p / 1280x720 / 2K */
export const extractResolutionPValue = (resolution: string): number | undefined => {
  const wxh = resolution.match(/(\d+)\s*[xX×]\s*(\d+)/)
  if (wxh) {
    return Math.min(Number(wxh[1]), Number(wxh[2]))
  }
  const pMatch = resolution.match(/(\d+)\s*[pP]/)
  if (pMatch) {
    return Number(pMatch[1])
  }
  const kMatch = resolution.match(/(\d+(?:\.\d+)?)\s*[kK]/)
  if (kMatch) {
    return Number(kMatch[1]) * 1000
  }
  return undefined
}

/** 把任意分辨率收成档位展示名：宽高取短边，2000 起收成 nK */
export const formatResolutionPLabel = (value: string): string => {
  const p = extractResolutionPValue(value)
  if (p == null) return value
  if (p >= 4000) return '4K'
  if (p >= 2000 && p % 1000 === 0) return `${p / 1000}K`
  return `${p}P`
}

type ResolutionOptionLike = {
  value: string
  label?: string
}

/** 按 value / 大小写 / 短边档位匹配选项，不限定某一档 */
export const matchResolutionOption = <T extends ResolutionOptionLike>(
  value: string | undefined,
  options: T[]
): T | undefined => {
  if (!value || !options.length) return undefined

  const trimmed = value.trim()
  const exact = options.find(option => option.value === trimmed)
  if (exact) return exact

  const upper = trimmed.toUpperCase()
  const caseInsensitive = options.find(option => String(option.value).toUpperCase() === upper)
  if (caseInsensitive) return caseInsensitive

  const pValue = extractResolutionPValue(trimmed)
  if (pValue == null) return undefined

  return options.find(option => {
    if (extractResolutionPValue(String(option.value)) === pValue) return true
    if (option.label && extractResolutionPValue(String(option.label)) === pValue) return true
    return false
  })
}

/**
 * 按钮 / 列表展示用。
 * 优先用后台 label；若 label/value 仍是宽高（任意档），收成对应 P / K 档位名。
 */
export const formatResolutionLabel = (
  value: string | undefined,
  options: ResolutionOptionLike[] = []
): string => {
  if (!value) return ''

  const matched = matchResolutionOption(value, options)
  const preferred = matched?.label?.trim()
  if (preferred && !isPixelWxHResolution(preferred)) {
    return preferred
  }

  const source = preferred || matched?.value || value
  if (isPixelWxHResolution(source)) {
    return formatResolutionPLabel(source)
  }
  return source
}
